import { useEffect, useState, type FunctionComponent } from 'react';
import type { HomePageImageFieldsFragment, VideoFieldsFragment } from '../../../__generated__/graphql-client-types';
import { NO_IMAGE_URL } from '../../../constants/home/general';
import { windowIsNotSmall } from '../../../helpers/breakpoints/breakpoint.helpers';
import { generateCloudinaryUrl, type CloudinaryOptions } from '../../../helpers/cloudinary-helper/cloudinary-helper';
import { type AnimatedSplashCallToAction } from '../../../types/home.types';
import { LinkButton } from '../../buttons/link-button/link-button.component';
import { EmbeddedVideo } from '../../common-components/embedded-video/embedded-video.component';
import { Link } from '../../common-components/link/link.component';
import { ctaButtonPosition, ctaForegroundSize, splashAspectRatio } from '../animated-splash/animated-splash.css';
import { MainImage } from '../home-splash/main-image/main-image.component';
import { translateAlignmentToStyles } from '../home-splash/splash-helpers/splash.helpers';

const SPLASH_WIDTH = 1280;
const SPLASH_HEIGHT = 512;

type CallToActionContentProps = {
	callToAction: AnimatedSplashCallToAction;
};

const CallToActionContent: FunctionComponent<CallToActionContentProps> = ({ callToAction }) => {
	const { ctaStyle, ctaSubtext, ctaTitle, foregroundTracking, text } = callToAction;
	const { url, tracking } = callToAction.link;
	const resolvedForegroundTracking = foregroundTracking ? foregroundTracking : tracking;
	const ctaForegroundColor = ctaStyle === 'LIGHT' ? 'bg-theme-primary-lighter' : 'bg-theme-primary-darker';
	const ctaSubtextColor = ctaStyle === 'LIGHT' ? 'theme-black' : 'theme-white';
	const buttonStyle = ctaStyle === 'LIGHT' ? 'PRIMARY' : 'SECONDARY';

	return (
		<div className="flex flex-column items-center justify-center overflow-hidden w-100 pa0 pa5-ns relative">
			<div className={`absolute z-1 ${ctaButtonPosition}`}>
				<div className="dn flex-l">
					<LinkButton buttonStyle={buttonStyle} size={'DEFAULT'} url={url} analyticsHook={tracking}>
						{text}
					</LinkButton>
				</div>
				<div className="flex dn-l">
					<LinkButton buttonStyle={buttonStyle} size={'SMALL'} url={url} analyticsHook={tracking}>
						{text}
					</LinkButton>
				</div>
			</div>
			<Link
				url={url}
				ariaLabel="foreground"
				analyticsHook={resolvedForegroundTracking}
				className="flex flex-column items-center overflow-hidden"
				automationHook="landing-page-section">
				<div className={`${ctaForegroundSize} ${ctaForegroundColor} br4`}>
					<div className={`ma5 ${ctaSubtextColor}`}>
						<h3 className="mt0 mb2 f3 f2-l fw2 ttu lh-title" style={{ letterSpacing: '2px' }}>
							{ctaTitle}
						</h3>
						<p className="mv0 f5 f4-l fw1">{ctaSubtext}</p>
					</div>
				</div>
			</Link>
		</div>
	);
};

const FALLBACK_IMAGE_OPTIONS: CloudinaryOptions = {
	width: SPLASH_WIDTH,
	height: SPLASH_HEIGHT,
	quality: 80,
	progressive: true,
	format: 'jpg',
	type: 'private'
};

export type AnimatedSplashProps = {
	image: HomePageImageFieldsFragment;
	fallbackImage: HomePageImageFieldsFragment;
	video?: VideoFieldsFragment | null;
	mobileImage?: HomePageImageFieldsFragment;
	backgroundTracking?: string | null;
	animatedSplashCallToAction: AnimatedSplashCallToAction;
};

export const AnimatedSplash: FunctionComponent<AnimatedSplashProps> = ({
	image,
	fallbackImage,
	video,
	mobileImage,
	backgroundTracking,
	animatedSplashCallToAction
}) => {
	const [showVideo, setShowVideo] = useState<boolean>(false);
	const { url, tracking } = animatedSplashCallToAction.link;
	const resolvedBackgroundTracking = backgroundTracking ? backgroundTracking : tracking;
	const fallbackImageSrc = generateCloudinaryUrl(fallbackImage.id, FALLBACK_IMAGE_OPTIONS);
	const hasNoImage = image.id === NO_IMAGE_URL;
	const dynamicCTAAlignmentStyle = translateAlignmentToStyles(animatedSplashCallToAction.alignment);

	useEffect(() => {
		const isNotSmall = windowIsNotSmall();
		if (isNotSmall) {
			setShowVideo(true);
		}
	}, []);

	return (
		<section className="flex flex-column items-center w-100 min-h-100 relative pointer bg-theme-white" data-testid="animated-splash">
			<div className="flex flex-column items-center overflow-hidden w-100 relative br4" style={{ maxHeight: SPLASH_HEIGHT }}>
				<div className="absolute dn flex-ns z-1" style={{ ...dynamicCTAAlignmentStyle }}>
					<CallToActionContent callToAction={animatedSplashCallToAction} />
				</div>
				<div className="absolute dn-ns flex z-1 mt6">
					<CallToActionContent callToAction={animatedSplashCallToAction} />
				</div>
				{hasNoImage ? (
					<div className={`relative w-100 ${splashAspectRatio}`}>
						<div className="absolute mh3 mh0-ns" style={{ inset: 0 }}>
							{/* TODO: Replace with dedicated Picture component once created */}
							<Link url={url}>
								<picture>
									{mobileImage && (
										<source
											media="(max-width: 767px)"
											srcSet={generateCloudinaryUrl(mobileImage.id, { type: 'private' })}
										/>
									)}
									{fallbackImage && <source media="(min-width: 768px)" srcSet={fallbackImageSrc} />}
									<img
										src={fallbackImageSrc}
										alt={fallbackImage.description}
										className="br4 max-h-100"
										width={SPLASH_WIDTH}
										height={SPLASH_HEIGHT}
									/>
								</picture>
							</Link>
						</div>
						{showVideo && video?.id && (
							<div className="absolute" style={{ inset: 0 }} data-testid="splash-video">
								<Link url={url} className="w-100 h-100">
									<div className="disableClickEvents">
										<EmbeddedVideo video={video} shouldLoadEagerly={true} />
									</div>
								</Link>
							</div>
						)}
					</div>
				) : image ? (
					<MainImage
						background={image}
						url={url}
						analyticsHook={resolvedBackgroundTracking}
						options={{ ...FALLBACK_IMAGE_OPTIONS, format: 'auto' }}
					/>
				) : null}
			</div>
		</section>
	);
};
